import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import WinnerSnapshot from "./WinnerSnapshot";

import * as UI from "./ui";

const INTERVAL_MS = 12000;

const Carousel = ({ flashContests, timezone }) => {
  const [ currentIndex, setCurrentIndex ] = useState(0);
  const length = flashContests.length;
  const indicatorList = useMemo(() => Array.from({ length }), [length]);
  const showControls = length > 1;
  const showDots = length < 6;
  const timer = useRef(null);

  const setupTimer = useCallback(() => {
    const tick = () => {
      setCurrentIndex(lastIndex => (lastIndex + 1) % length);
    };

    timer.current = setInterval(tick, INTERVAL_MS);
    return () => { clearInterval(timer.current) }
  }, [length]);

  const resetTimer = useCallback(() => {
    clearInterval(timer.current);
    // Wait a full interval before we start the clock ticking again.
    // Essentially doubles the interval for a manually accessed flashContest.
    timer.current = setTimeout(setupTimer, INTERVAL_MS);
  }, [setupTimer]);

  useEffect(setupTimer, [ flashContests, setupTimer ]);

  const handlePrevious = useCallback(fromIndex => () => {
    resetTimer();

    if(fromIndex - 1 < 0) {
      return setCurrentIndex(length - 1);
    }

    return setCurrentIndex(fromIndex - 1);
  }, [resetTimer, setCurrentIndex, length]);

  const handleNext = useCallback(fromIndex => () => {
    resetTimer();

    return setCurrentIndex((fromIndex + 1) % length);
  }, [resetTimer, setCurrentIndex, length]);

  const handleSelect = useCallback(index => () => {
    resetTimer();
    setCurrentIndex(index);
  }, [resetTimer, setCurrentIndex]);

  if(length === 0) {
    return null;
  }

  return (
    <UI.CarouselLayout role="region" aria-roledescription="carousel" aria-label="Flash Contest Winners">
      {showControls && <UI.LeftPaddle onClick={handlePrevious(currentIndex)} aria-label="Previous Winner" aria-hidden="false" tabIndex={0} />}
      <UI.WinnerList role="list">
        { flashContests.map((flashContest, index) => (
          <UI.FlashContestWinnerItem
            key={flashContest.__typename + flashContest.id}
            $visible={index === currentIndex}
            role="listitem"
            aria-current={index === currentIndex ? "true" : undefined}
            aria-label="Winner Summary"
          >
            <WinnerSnapshot flashContest={flashContest} timezone={timezone} />
          </UI.FlashContestWinnerItem>
        ))}
      </UI.WinnerList>
      {showControls && (
        <React.Fragment>
          <UI.RightPaddle onClick={handleNext(currentIndex)} aria-label="Next Winner" aria-hidden="false" tabIndex={0} />
          {showDots && (
            <UI.IndicatorDots role="list" aria-label="Flash Contest Selection (Most recent first)">
              { indicatorList.map((_, index) => (
                <UI.IndicatorDot
                  key={`FlashContestIndicatorDot-${index}`}
                  $selected={index === currentIndex}
                  onClick={handleSelect(index)}
                  tabIndex={0}
                  role="listitem"
                  aria-label={`Select flash contest ${index + 1}`}
                  aria-current={index === currentIndex ? "true" : undefined}
                />
              )) }
            </UI.IndicatorDots>
          )}
        </React.Fragment>
      )}
    </UI.CarouselLayout>
  );
}

export default Carousel;
